/**
 * The rolling set of recent departure pulses (spec §6).
 *
 * Corridor state is stable and comparable; activity is transient and
 * unaggregated. They are opposite requirements and must not share a layer — a
 * corridor line that flickers with activity is exactly the thing that cannot be
 * measured against another corridor. So pulses live in their own buffer and
 * their own layer, and expire on their own clock rather than on the tick.
 *
 * Spec §13 Q4 asks at what scenario size pulses stop reading as life and start
 * reading as noise. That question is answered by the two constants below, which
 * is why they live in one small pure module rather than inline in the layer.
 *
 * Pure module: no Vue, no Leaflet, no store.
 */

/** How long a pulse stays on the map. Long enough to notice, short enough to clear. */
export const PULSE_TTL_MS = 4000

/**
 * Hard cap on concurrent pulses. A busy scenario can fire many departures in one
 * tick, and past a certain density the map reads as static rather than alive.
 */
export const PULSE_MAX = 40

/**
 * Adds a pulse, dropping the oldest if the buffer is full and ignoring one whose
 * id is already present.
 *
 * Returns a new array rather than mutating: the buffer is handed to a Vue store
 * ref, and in-place mutation of an array behind a `ref` does not reliably notify.
 *
 * @param {Array<{id: string, at: number}>} buffer
 * @param {{id: string, at: number}} pulse
 * @param {{max?: number}} [options]
 * @returns {Array} the new buffer
 */
export function pushPulse(buffer, pulse, { max = PULSE_MAX } = {}) {
  const current = buffer ?? []
  if (current.some(p => p.id === pulse.id)) return current

  const next = [...current, pulse]
  return next.length > max ? next.slice(next.length - max) : next
}

/**
 * Drops pulses older than the TTL. Cheap enough to call on every push, which is
 * what keeps a long-idle buffer from holding stale entries forever.
 *
 * @param {Array<{id: string, at: number}>} buffer
 * @param {number} nowMs
 * @param {number} [ttlMs]
 * @returns {Array} the pruned buffer — the same array when nothing expired
 */
export function prunePulses(buffer, nowMs, ttlMs = PULSE_TTL_MS) {
  const current = buffer ?? []
  const live = current.filter(p => nowMs - p.at < ttlMs)
  return live.length === current.length ? current : live
}
